import React, { useEffect, useState, useRef } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import {
  Spin,
  Descriptions,
  Timeline,
  Collapse,
  Row,
  Tag,
  Typography,
  Button,
  Popover,
  message,
  Tooltip,
  Space,
  Select,
  Col,
} from "antd";
import {
  CopyOutlined,
  UpOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { useParams } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { SessionService } from "../../services/SessionService";
import {
  Session,
  Turn,
  EvaluateResult,
  ToolCall,
} from "../../model_gen/session";
import { ChatCompletionRequestMetadata } from "../../model_gen/chat_metadata";
import { formatCurrency, formatTextOrJson } from "../../utils/FormatUtils";
import { calculateScore } from "./SessionUtils";
import { keyframes } from "styled-components";
import styled from "styled-components";
import { TestSet } from "../../model_gen/evaluation";
import TestSetContextMenu from "../TestSet/TestSetContextMenu";
import { RequestsService } from "../../services/RequestsService";
import { TestSetService } from "../../services/TestSetService";
import { Link } from "react-router-dom";
import { RequestViewButton } from "../shared/button/Buttons";

const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

interface SessionOverviewProps {
  sessionId?: string;
  applicationId?: string;
  refreshTrigger?: number;
  requestId?: string;
}

const styles = {
  container: {
    padding: "24px",
    maxWidth: "1200px",
    margin: "0 auto",
    minHeight: "calc(100vh - 64px)",
  },
  header: {
    marginBottom: "32px",
  },
  contentCard: {
    background: "#fff",
    padding: "24px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    border: "1px solid #f0f0f0",
  },
  tableContainer: {
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
  },
  detailsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  timelineContainer: {
    marginTop: "32px",
  },
  timelineItem: {
    marginTop: "48px",
  },
  timestampLabel: {
    fontSize: "12px",
  },
  turnCollapse: {
    width: "auto",
    padding: "20px 30px",
    borderRadius: "16px",
  },
  tagStyle: {
    padding: "4px 10px",
    borderRadius: "4px",
    fontSize: "12px",
  },
  monospaceFont: {
    fontFamily: "monospace",
    fontSize: "13px",
  },
  summaryCard: {
    marginTop: 12,
    padding: 12,
    background: "rgba(0, 122, 255, 0.05)",
    borderRadius: 8,
    border: "1px solid rgba(0, 122, 255, 0.1)",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "calc(100vh - 200px)",
  },
  messagesCollapse: {
    marginTop: 6,
    backgroundColor: "transparent",
  },
  messagePanel: {
    marginTop: 10,
    backgroundColor: "white",
    border: "none",
  },
  requestMetadata: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#00000080",
    flex: 1,
  },
  statusTag: {
    marginRight: 20,
  },
  requestTooltip: {
    maxWidth: "600px",
    width: "600px",
    borderRadius: "12px",
    boxShadow: "0 3px 10px rgba(0, 0, 0, 0.1)",
  },
  viewRequestButton: {
    borderRadius: "6px",
    color: "#1890ff",
    display: "flex",
    alignItems: "center",
    transition: "all 0.2s",
  },
};

const pulseAnimation = keyframes`
  0%, 100% {
    background-color: rgba(0, 0, 0, 0.02);
  }
  50% {
    background-color: rgba(255, 165, 0, 0.3);
  }
`;

const HighlightedCollapse = styled(Collapse)<{ highlight: boolean }>`
  transition: background-color 0.3s ease-in-out;

  &.highlight {
    animation: ${pulseAnimation} 1s ease-in-out 2;
  }
`;

const formatSessionId = (sessionId: string) => {
  if (!sessionId) return "";
  return `...${sessionId.slice(-8)}`;
};

const SessionOverview: React.FC<SessionOverviewProps> = ({
  sessionId: propSessionId,
  applicationId: propApplicationId,
  refreshTrigger,
  requestId,
}) => {
  const { apiKey } = usePermissions();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [session, setSession] = useState<Session | null>(null);
  const wsRef = useRef<ReconnectingWebSocket | null>(null);
  const lastMessageTimeRef = useRef<Date>(new Date());
  const timelineRef = useRef<HTMLDivElement>(null);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [isLoadingOlder, setIsLoadingOlder] = useState<boolean>(false);
  const [testSetsByAction, setTestSetsByAction] = useState<
    Record<string, TestSet[]>
  >({});
  const [summaryLoading, setSummaryLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const [requestMetadata, setRequestMetadata] = useState<{
    [key: string]: ChatCompletionRequestMetadata;
  }>({});
  const [unselectedIntents, setUnselectedIntents] = useState<Set<string>>(
    new Set()
  );
  const [availableIntents, setAvailableIntents] = useState<string[]>([]);

  // Get parameters from URL if not provided as props
  const { sessionId: paramSessionId, applicationId: paramApplicationId } =
    useParams<{ sessionId: string; applicationId: string }>();

  // Use props if available, otherwise use URL parameters
  const sessionId = propSessionId || paramSessionId;
  const applicationId = propApplicationId || paramApplicationId;

  useEffect(() => {
    if (applicationId && sessionId) {
      loadSession();
    }
    return openWebsocket();
  }, [sessionId, applicationId, apiKey, refreshTrigger]);

  useEffect(() => {
    if (applicationId) {
      loadTestSets();
    }
  }, [applicationId, apiKey]);

  // Add global CSS for button styling
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .details-button:hover {
        background-color: #f0f7ff !important;
        box-shadow: 0 2px 4px rgba(24, 144, 255, 0.1) !important;
      }
      .interactive-row:hover td {
        background-color: #f5f8ff !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const loadSession = () => {
    SessionService.getSession(
      applicationId,
      sessionId,
      0,
      25,
      setIsLoading,
      apiKey
    ).subscribe(
      (fetchedSession: Session) => {
        setSession(fetchedSession);
      },
      (error: any) => {
        console.error(error);
      }
    );
  };

  const loadOlderRequests = () => {
    setIsLoadingOlder(true);
    const nextPage = page + 1;
    SessionService.getSession(
      applicationId,
      sessionId,
      nextPage,
      25,
      () => {},
      apiKey
    ).subscribe(
      (fetchedSession: Session) => {
        setSession((prevSession) => {
          if (!prevSession) return fetchedSession;
          const existingRequestIds = new Set(
            prevSession.timeline.map((turn) => turn.request?.request_id)
          );
          const newTurns = fetchedSession.timeline.filter(
            (turn) => !existingRequestIds.has(turn.request?.request_id)
          );
          return {
            ...fetchedSession,
            timeline: [...newTurns, ...prevSession.timeline],
          };
        });
        setPage(nextPage);
        setIsLoadingOlder(false);
      },
      (error: any) => {
        console.error(error);
        setIsLoadingOlder(false);
      }
    );
  };

  const openWebsocket = () => {
    closeWsConnection();

    const ws: any = new ReconnectingWebSocket(
      `wss://09hidyy627.execute-api.us-west-2.amazonaws.com/production?key=${encodeURIComponent(
        sessionId
      )}&type=SESSION_UPDATE`
    );
    wsRef.current = ws;

    ws.addEventListener("message", (message: any) => {
      lastMessageTimeRef.current = new Date();
      const data = JSON.parse(message.data);
      if (data.event_type === "SESSION_UPDATE") {
        handleNewSessionTurn(data);
      }
    });

    const checkInterval = setInterval(() => {
      const now = new Date();
      const diffInMinutesWS =
        (now.getTime() - lastMessageTimeRef.current.getTime()) / (1000 * 60);

      if (diffInMinutesWS >= 1) {
        if (wsRef.current) {
          wsRef.current.reconnect(1000, "Manual reconnect");
          lastMessageTimeRef.current = new Date();
        }
      }
    }, 1000 * 5);

    return () => {
      if (checkInterval) clearInterval(checkInterval);
      closeWsConnection();
    };
  };

  const closeWsConnection = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  const handleNewSessionTurn = (data: any) => {
    const turn: Turn = JSON.parse(data.event_data);
    setSession((prevSession) => {
      if (!prevSession) return prevSession;
      const updatedTimeline = [...prevSession.timeline];
      const turnIndex = updatedTimeline.findIndex(
        (t) => t.request?.request_id === turn.request?.request_id
      );
      if (turnIndex !== -1) {
        // Update existing turn
        updatedTimeline[turnIndex] = {
          ...updatedTimeline[turnIndex],
          ...turn,
        };
      } else {
        // Add new turn
        updatedTimeline.unshift(turn);
      }

      // Update available intents with the new turn
      if (turn.request?.action_type) {
        updateAvailableIntents(updatedTimeline);
      }

      return { ...prevSession, timeline: updatedTimeline };
    });
  };

  const removeDuplicateTurns = (timeline: Turn[]): Turn[] => {
    const uniqueTurns = new Map<string, Turn>();
    timeline.forEach((turn) => {
      if (turn.request?.request_id) {
        uniqueTurns.set(turn.request.request_id, turn);
      }
    });
    return Array.from(uniqueTurns.values());
  };

  const sortedTimeline = React.useMemo(() => {
    if (!session?.timeline) return [];
    const uniqueTimeline = removeDuplicateTurns(session.timeline);
    return uniqueTimeline.sort((a, b) => {
      const dateA = new Date(a.request?.date_created || 0);
      const dateB = new Date(b.request?.date_created || 0);
      return dateA.getTime() - dateB.getTime(); // Changed to ascending order
    });
  }, [session?.timeline]);

  const copyRequestId = (requestId: string) => {
    navigator.clipboard.writeText(requestId);
    message.success("Request ID copied to clipboard");
  };

  useEffect(() => {
    if (requestId && session) {
      setHighlightedId(requestId);
      setTimeout(() => {
        const requestElement = document.querySelector(
          `[data-request-id="${requestId}"]`
        );
        if (requestElement) {
          requestElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);

      // Remove highlight after animation completes
      const timer = setTimeout(() => {
        setHighlightedId(null);
      }, 2000); // 2 seconds for 2 pulses

      return () => clearTimeout(timer);
    }
  }, [requestId, session]);

  const formatDateTime = (
    date: number | undefined,
    prevDate: number | undefined,
    nextDate: number | undefined
  ) => {
    if (!date) return { display: "", full: "" };
    const currentMoment = moment(date);
    const prevMoment = prevDate ? moment(prevDate) : null;
    const nextMoment = nextDate ? moment(nextDate) : null;

    const showFullDate =
      (prevMoment && !currentMoment.isSame(prevMoment, "day")) ||
      (nextMoment && !currentMoment.isSame(nextMoment, "day"));

    const fullDateTime = currentMoment.format("MMM D, YYYY HH:mm:ss A");
    const displayDateTime = showFullDate
      ? fullDateTime
      : currentMoment.format("HH:mm:ss");

    return { display: displayDateTime, full: fullDateTime };
  };

  const handleViewRequestClick = (requestId: string) => {
    window.open(
      `/application/${applicationId}/session/${encodeURIComponent(
        sessionId
      )}/request/${requestId}`,
      "_blank"
    );
  };

  const loadTestSets = () => {
    TestSetService.getTestSets(null, apiKey).subscribe(
      (response: any) => {
        // response.items is already the array of test sets
        const testSets = response || [];
        // Group test sets by application_action_id
        const groupedTestSets = testSets.reduce(
          (acc: Record<string, TestSet[]>, testSet: TestSet) => {
            const actionType = testSet?.intent_group?.intent_name;
            if (!acc[actionType]) {
              acc[actionType] = [];
            }
            acc[actionType].push(testSet);
            return acc;
          },
          {}
        );

        setTestSetsByAction(groupedTestSets);
      },
      (error: any) => {
        console.error("Failed to load test sets:", error);
        message.error("Failed to load test sets");
      }
    );
  };

  const handleTestSetUpdate = (
    updatedRequestId: string,
    updatedTestSetIds: string[]
  ) => {
    setSession((prevSession) => {
      if (!prevSession) return prevSession;

      const updatedTimeline = prevSession.timeline.map((t) => {
        if (t.request?.request_id === updatedRequestId) {
          return {
            ...t,
            request: {
              ...t.request,
              metadata: {
                ...t.request.metadata,
                test_set_ids: JSON.stringify(updatedTestSetIds),
              },
            },
          };
        }
        return t;
      });

      return {
        ...prevSession,
        timeline: updatedTimeline,
      };
    });
    loadTestSets();
  };

  const fetchRequestSummary = (turn: Turn) => {
    const requestId = turn.request?.request_id;
    if (!requestId || requestMetadata[requestId]) return;

    setSummaryLoading((prev) => ({ ...prev, [requestId]: true }));

    RequestsService.getRequestMetadata(turn.request, null, apiKey).subscribe(
      (result: any) => {
        if (result.request_id) {
          setRequestMetadata((prev) => ({
            ...prev,
            [result.request_id]: result.metadata,
          }));
        }
        setSummaryLoading((prev) => ({ ...prev, [requestId]: false }));
      },
      (error: any) => {
        console.error("Failed to fetch metadata:", error);
        setSummaryLoading((prev) => ({ ...prev, [requestId]: false }));
      }
    );
  };

  const getExistingTestSetIds = (metadata?: any) => {
    try {
      return metadata?.test_set_ids
        ? JSON.parse(metadata.test_set_ids).map(String)
        : [];
    } catch (e) {
      console.error("Error parsing test_set_ids:", e);
      return [];
    }
  };

  const updateAvailableIntents = React.useCallback((timeline: Turn[]) => {
    const newIntents = new Set<string>();
    timeline.forEach((turn) => {
      if (turn.request?.action_type) {
        newIntents.add(turn.request.action_type);
      }
    });

    setAvailableIntents((prev) => {
      const allIntents = Array.from(
        new Set([...prev, ...Array.from(newIntents)])
      );
      return allIntents.sort();
    });
  }, []);

  // Update available intents whenever the timeline changes
  useEffect(() => {
    if (session?.timeline) {
      updateAvailableIntents(session.timeline);
    }
  }, [session?.timeline, updateAvailableIntents]);

  // Handle intent selection changes
  const handleIntentSelectionChange = (selectedValues: string[]) => {
    const newUnselected = new Set(
      availableIntents.filter((intent) => !selectedValues.includes(intent))
    );
    setUnselectedIntents(newUnselected);
  };

  // Update the filtered timeline to exclude unselected intents
  const filteredTimeline = React.useMemo(() => {
    return sortedTimeline.filter(
      (turn) =>
        turn.request?.action_type &&
        !unselectedIntents.has(turn.request.action_type)
    );
  }, [sortedTimeline, unselectedIntents]);

  if (isLoading && session === null) {
    return (
      <div style={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  const getFaultSummary = (turn: Turn) => {
    let faultSummary = turn.eval_request?.eval_results_set?.evaluate_summary;
    let suggestedCorrection = "";
    turn?.eval_request?.eval_results_set?.evaluation_results?.forEach(
      (val: EvaluateResult) => {
        if (!faultSummary && val.status == "FAULT") {
          faultSummary = val.description;
        }
        if (!suggestedCorrection && val.status == "FAULT") {
          suggestedCorrection = val.meta.correction as string;
        }
      }
    );
    const correctionField = formatTextOrJson(
      turn.response?.correction_applied
        ? turn.eval_request?.text_content
        : suggestedCorrection
    );
    return (
      <>
        <Descriptions colon={false} column={1}>
          <Descriptions.Item label="Reason">{faultSummary}</Descriptions.Item>
          <Descriptions.Item
            label={
              turn.response?.correction_applied
                ? "Original"
                : "Suggested Correction"
            }>
            {correctionField}
          </Descriptions.Item>
        </Descriptions>
      </>
    );
  };

  const content = (
    <div className="session-container" ref={timelineRef}>
      {/* Header */}
      <div style={styles.header}>
        <Row align="middle">
          <Col>
            <Space direction="vertical" size={4}>
              <Typography.Text type="secondary">
                {applicationId ? (
                  <Link to={`/application/${applicationId}`}>Application</Link>
                ) : (
                  "Application"
                )}{" "}
                / Session {formatSessionId(session?.session_id || "")}
              </Typography.Text>
              <Typography.Title
                level={2}
                style={{ margin: 0, fontWeight: 600 }}>
                Session Details
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
                View session details, requests, and messages
              </Typography.Text>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Session Information Card */}
      <div style={styles.contentCard}>
        <div style={styles.detailsHeader}>
          <Typography.Title level={5} style={{ margin: 0, fontWeight: 600 }}>
            Session Information
          </Typography.Title>
        </div>

        {session ? (
          <Descriptions colon={false} column={12} layout="vertical">
            <Descriptions.Item label="Session ID" span={4}>
              <div style={{ display: "flex", alignItems: "center" }}>
                {formatSessionId(session?.session_id)}
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => {
                    navigator.clipboard.writeText(session?.session_id || "");
                    message.success("Session ID copied to clipboard");
                  }}
                  size="small"
                  style={{ marginLeft: "8px" }}
                />
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Date Created" span={3}>
              {moment(session?.date_created).format("MM/DD/YYYY hh:mm:ss A")}
            </Descriptions.Item>
            <Descriptions.Item label="Cost" span={2}>
              {session.cost > 0 ? formatCurrency(session.cost) : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Health" span={1}>
              100
            </Descriptions.Item>
            <Descriptions.Item label="Score" span={2}>
              {calculateScore(session)}
            </Descriptions.Item>
            <Descriptions.Item label="Intents" span={12}>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder="Select intents to display"
                value={availableIntents.filter(
                  (intent) => !unselectedIntents.has(intent)
                )}
                onChange={handleIntentSelectionChange}
                options={availableIntents.map((intent) => ({
                  label: intent,
                  value: intent,
                }))}
              />
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Paragraph>
            Session still processing; please try again later
          </Paragraph>
        )}
      </div>

      {/* Timeline Section */}
      <div style={{ ...styles.contentCard, marginTop: "24px" }}>
        <div style={styles.detailsHeader}>
          <Typography.Title level={5} style={{ margin: 0, fontWeight: 600 }}>
            Timeline
          </Typography.Title>
        </div>

        <Timeline
          pending="Listening..."
          key={session?.session_id}
          style={styles.timelineContainer}>
          {filteredTimeline.length > 0 && !filteredTimeline[0].first && (
            <Timeline.Item
              dot={
                <Button
                  icon={
                    isLoadingOlder ? (
                      <LoadingOutlined style={{ fontSize: "16px" }} />
                    ) : (
                      <UpOutlined style={{ fontSize: "16px" }} />
                    )
                  }
                  onClick={loadOlderRequests}
                  disabled={isLoadingOlder}
                  style={{ marginBottom: 16 }}
                />
              }>
              More requests available
            </Timeline.Item>
          )}
          {filteredTimeline.map((turn: Turn, index: number) => {
            const prevTurn = index > 0 ? filteredTimeline[index - 1] : null;
            const nextTurn =
              index < filteredTimeline.length - 1
                ? filteredTimeline[index + 1]
                : null;
            const { display: dateTimeString, full: fullDateTimeString } =
              formatDateTime(
                turn.request?.date_created,
                prevTurn?.request?.date_created,
                nextTurn?.request?.date_created
              );
            let faultySentinel = "";
            let faultCount = 0;
            turn.eval_request?.eval_results_set?.evaluation_results?.forEach(
              (evalResult: EvaluateResult) => {
                if (evalResult.status == "FAULT" && faultySentinel == "") {
                  faultySentinel = evalResult.sentinel_name || "";
                }
                if (evalResult.status == "FAULT") {
                  faultCount += 1;
                }
              }
            );
            const toolCalls =
              turn.response?.choices?.[0]?.message?.tool_calls?.map(
                (toolCall: ToolCall) => {
                  return (
                    <Row key={toolCall.id}>
                      <pre style={{ whiteSpace: "pre-wrap" }}>
                        <code>
                          {toolCall?.function?.name}(
                          {toolCall?.function?.arguments})
                        </code>
                      </pre>
                    </Row>
                  );
                }
              );
            const requestMetadata = JSON.parse(
              (turn.request?.metadata?._maitai_request_meta as string) || "{}"
            );

            return (
              <Timeline.Item
                key={turn.request?.request_id}
                data-request-id={turn.request?.request_id}
                dot={
                  <Tooltip title={fullDateTimeString}>
                    <Text style={styles.timestampLabel}>{dateTimeString}</Text>
                  </Tooltip>
                }
                style={styles.timelineItem}
                color="black">
                <HighlightedCollapse
                  style={styles.turnCollapse}
                  bordered={false}
                  defaultActiveKey={[]}
                  expandIconPosition="end"
                  highlight={turn.request?.request_id === highlightedId}
                  className={
                    turn.request?.request_id === highlightedId
                      ? "highlight"
                      : ""
                  }>
                  <Panel
                    header={
                      <div style={{ width: "100%" }}>
                        <Row align="middle">
                          <Tag style={styles.tagStyle}>
                            {turn.request?.action_type}
                          </Tag>
                          {turn.response?.correction_applied ? (
                            <Popover
                              content={getFaultSummary(turn)}
                              trigger="hover">
                              <Tag color="success" style={styles.statusTag}>
                                Corrected
                              </Tag>
                            </Popover>
                          ) : faultCount > 0 ? (
                            <Popover
                              content={getFaultSummary(turn)}
                              trigger="hover">
                              <Tag color="error" style={styles.statusTag}>
                                {faultCount > 1 ? "FAULTS" : "FAULT"}
                              </Tag>
                            </Popover>
                          ) : (
                            <CheckCircleOutlined
                              style={{ color: "green", marginRight: 20 }}
                            />
                          )}
                          <Text style={styles.requestMetadata}>
                            {turn.response?.response_time &&
                            turn.response.response_time > 0 ? (
                              <span style={{ marginRight: 20 }}>
                                {turn.response?.response_time}ms
                              </span>
                            ) : null}
                            <span style={{ marginRight: 20 }}>
                              Tokens: {turn.response?.usage?.prompt_tokens}
                              {" > "}
                              {turn.response?.usage?.completion_tokens}
                            </span>
                            {turn.response?.usage?.cost &&
                            turn.response.usage.cost > 0 ? (
                              <span style={{ marginRight: 20 }}>
                                Cost: {formatCurrency(turn.response.usage.cost)}
                              </span>
                            ) : null}
                            {parseInt(
                              (turn?.request?.metadata?.reruns as string) || "0"
                            ) > 0 && (
                              <Tag color="blue" style={{ marginLeft: "8px" }}>
                                {String(turn.request.metadata.reruns)} rerun
                                {parseInt(
                                  turn.request.metadata.reruns as string
                                ) > 1
                                  ? "s"
                                  : ""}
                              </Tag>
                            )}
                          </Text>
                          {toolCalls?.length > 0 && (
                            <Tag style={{ marginRight: 20 }}>Tool Call</Tag>
                          )}
                          <Space>
                            <RequestViewButton
                              apiKey={apiKey}
                              request={turn.request}
                              size="small"
                              buttonStyle={styles.viewRequestButton}
                              showIcon={false}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewRequestClick(
                                  turn.request?.request_id || ""
                                );
                              }}>
                              View Request
                            </RequestViewButton>
                            <Tooltip title="Copy Request ID">
                              <Button
                                icon={<CopyOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyRequestId(turn.request?.request_id || "");
                                }}
                                size="small"
                              />
                            </Tooltip>
                            {turn.request && (
                              <TestSetContextMenu
                                applicationId={applicationId}
                                applicationActionType={
                                  turn.request.action_type || ""
                                }
                                requestId={turn.request.request_id || ""}
                                testSets={
                                  turn.request.action_type
                                    ? [
                                        ...(testSetsByAction[
                                          turn.request.action_type
                                        ] || []),
                                      ].sort((a, b) => b.id - a.id)
                                    : []
                                }
                                existingTestSetIds={getExistingTestSetIds(
                                  turn.request.metadata
                                )}
                                onUpdate={handleTestSetUpdate}>
                                <Button
                                  icon={<PlusOutlined />}
                                  size="small"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                />
                              </TestSetContextMenu>
                            )}
                          </Space>
                        </Row>
                        {requestMetadata?.summary && (
                          <Row style={{ marginTop: 8, marginBottom: 8 }}>
                            <div style={styles.summaryCard}>
                              <InfoCircleOutlined
                                style={{ color: "#007AFF", fontSize: "16px" }}
                              />
                              <Text
                                style={{
                                  color: "#2C2C2E",
                                  fontSize: "14px",
                                  lineHeight: "20px",
                                  margin: 0,
                                  flex: 1,
                                }}>
                                {requestMetadata.summary}
                              </Text>
                            </div>
                          </Row>
                        )}
                        <Row
                          style={{
                            fontSize: 14,
                            fontWeight: 500,
                            whiteSpace: "pre-wrap",
                          }}>
                          <span style={{ marginTop: 8 }}>
                            {formatTextOrJson(
                              turn.response?.choices?.[0]?.message?.content
                            )}
                            {turn.request?.error && (
                              <Text style={{ color: "red" }}>
                                {turn.request?.error}
                              </Text>
                            )}
                          </span>
                        </Row>
                        {toolCalls}
                      </div>
                    }
                    key="1">
                    <Row style={{ marginTop: 20, width: "100%" }}>
                      <Descriptions
                        colon={false}
                        column={6}
                        layout="vertical"
                        style={{ width: "100%" }}
                        labelStyle={{
                          fontWeight: 500,
                          color: "#8c8c8c",
                          fontSize: "13px",
                        }}
                        contentStyle={{ fontWeight: 500, fontSize: "14px" }}>
                        <Descriptions.Item label="Inference" span={1}>
                          {turn.request?.inference_location == "SERVER"
                            ? "Server"
                            : "Client"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Model" span={1}>
                          {turn.request?.params?.model || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Temperature" span={1}>
                          {turn.request?.params?.temperature || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Streaming" span={1}>
                          {turn.request?.params?.stream ? "On" : "Off"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Evaluations" span={1}>
                          {turn.eval_request?.id ? "On" : "Off"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Corrections" span={1}>
                          {turn.request?.apply_corrections ? "On" : "Off"}
                        </Descriptions.Item>
                      </Descriptions>
                    </Row>
                  </Panel>
                </HighlightedCollapse>
              </Timeline.Item>
            );
          })}
        </Timeline>
      </div>
    </div>
  );

  // If both sessionId and applicationId are provided as props, render without Card
  if (propSessionId && propApplicationId) {
    return content;
  }

  // Otherwise, wrap in a Card for standalone view
  return (
    <div style={styles.container}>
      <div style={styles.contentCard}>
        <div style={styles.detailsHeader}>
          <Typography.Title level={3} style={{ margin: 0, fontWeight: 600 }}>
            Session Overview
          </Typography.Title>
        </div>
        {content}
      </div>
    </div>
  );
};

export default SessionOverview;
